import React, { useEffect, useState } from 'react';
import * as api from '../api';
import { IconClose, IconPlusCircle } from '../icons';
import { plural, fmtCount } from './util';
import './search.css';

// fetchNext answers are cached by the server for an hour; keep a small
// client copy so toggling chips does not refetch.
const CACHE_MAX = 40;
const cache = new Map();
const remember = (k, v) => {
  cache.delete(k);
  cache.set(k, v);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
};

const fmtPct = (p) => (Number.isFinite(p) ? (p >= 10 ? Math.round(p) : Math.round(p * 10) / 10) + '%' : '');

// "What came next" after a song or an into-run (B.4): horizontal bars, one
// per next song (n and pct), plus the share of times the set ended there.
// Clicking a bar calls onExtend(key), which appends it as a linked chip.
// Optional (additive): onClose, disabledReason (bars inert, reason shown).
export default function WhatCameNext({ band, seq, onExtend, onClose, disabledReason }) {
  const keys = (Array.isArray(seq) ? seq : [seq]).filter((k) => typeof k === 'string' && k);
  const bandKey = band && (typeof band === 'string' ? band : band.key);
  const sig = bandKey + '|' + keys.join('>');
  const [state, setState] = useState({ sig: null, data: null, error: null });

  useEffect(() => {
    if (!bandKey || !keys.length) return undefined;
    if (cache.has(sig)) {
      setState({ sig, data: cache.get(sig), error: null });
      return undefined;
    }
    let live = true;
    setState({ sig, data: null, error: null });
    api
      .fetchNext(band, keys, 12)
      .then((d) => {
        remember(sig, d);
        if (live) setState({ sig, data: d, error: null });
      })
      .catch((e) => {
        if (live) setState({ sig, data: null, error: (e && e.userMessage) || 'Could not run that search. Try again in a moment.' });
      });
    return () => {
      live = false;
    };
    // `sig` captures band and keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (!keys.length) return null;
  const current = state.sig === sig;
  const data = current ? state.data : null;
  const error = current ? state.error : null;
  const loading = !data && !error;
  const next = data && Array.isArray(data.next) ? data.next.filter((x) => x && x.key) : [];
  const seqNames = data && Array.isArray(data.seq) && data.seq.length ? data.seq.map((s) => s.name || s.key) : null;
  // pct is 100 * n / occurrences, so any row gives the occurrence scale for endOfSet
  const ref = next.find((x) => x.n > 0 && x.pct > 0);
  const endOfSet = data && Number(data.endOfSet) > 0 ? Number(data.endOfSet) : 0;
  const endPct = endOfSet && ref ? (endOfSet * ref.pct) / ref.n : null;
  const maxPct = Math.max(1e-9, ...next.map((x) => Number(x.pct) || 0), endPct || 0);
  const width = (p) => Math.max(1.5, (100 * (Number(p) || 0)) / maxPct) + '%';

  return (
    <section className="ss-next" aria-label="What came next">
      <div className="ss-next-head">
        <div className="ss-next-titles">
          <div className="ss-next-title">What came next</div>
          <div className="ss-next-sub">
            {seqNames ? 'After ' + seqNames.join(' into ') : 'After this song'}
            {data && Number(data.shows) > 0 ? ', in ' + plural(Number(data.shows), 'show') : ''}
          </div>
        </div>
        {onClose && (
          <button type="button" className="icon-btn ss-mini" onClick={onClose} title="Hide" aria-label="Hide what came next">
            <IconClose size={14} />
          </button>
        )}
      </div>
      {loading && (
        <div className="ss-next-list" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skel ss-next-skel" />
          ))}
        </div>
      )}
      {error && <div className="ss-note">{error}</div>}
      {data && next.length === 0 && !endOfSet && <div className="ss-note">Nothing to show for this song yet.</div>}
      {data && (next.length > 0 || endOfSet > 0) && (
        <div className="ss-next-list">
          {next.map((x) => (
            <button
              key={x.key}
              type="button"
              className="ss-bar"
              disabled={!!disabledReason}
              onClick={() => onExtend && onExtend(x.key, x.name)}
              title={
                'Came next ' +
                plural(Number(x.n) || 0, 'time') +
                (Number(x.intoN) > 0 ? ', played straight into it ' + plural(Number(x.intoN), 'time') : '') +
                (disabledReason ? '' : '. Click to add it to the chain.')
              }
            >
              <span className="ss-bar-name">{x.name || x.key}</span>
              <span className="ss-bar-track" aria-hidden="true">
                <span className="ss-bar-fill" style={{ width: width(x.pct) }} />
              </span>
              <span className="ss-bar-val mono">
                {fmtCount(x.n)} <span className="dim">{fmtPct(Number(x.pct))}</span>
              </span>
              <span className="ss-bar-add" aria-hidden="true">
                <IconPlusCircle size={14} />
              </span>
            </button>
          ))}
          {endOfSet > 0 && (
            <div className="ss-bar ss-bar-end" title={'The set ended there ' + plural(endOfSet, 'time')}>
              <span className="ss-bar-name">End of set</span>
              <span className="ss-bar-track" aria-hidden="true">
                {endPct != null && <span className="ss-bar-fill" style={{ width: width(endPct) }} />}
              </span>
              <span className="ss-bar-val mono">
                {fmtCount(endOfSet)} {endPct != null && <span className="dim">{fmtPct(endPct)}</span>}
              </span>
              <span className="ss-bar-add" aria-hidden="true" />
            </div>
          )}
        </div>
      )}
      {disabledReason && data && next.length > 0 && <div className="ss-note">{disabledReason}</div>}
    </section>
  );
}
